import { ApplicationRef, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { AlertType } from '@feature/alert/alert.component';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, isObservable } from 'rxjs';
import { filter, first } from 'rxjs/operators';

import { snackBarDisplayTimeInMs, snackBarErrorDisplayTimeInMs } from './snack-bar';
import { SnackBarComponent } from './snack-bar.component';

const ROUTES_THAT_CLEAR_CARRYOVER_SNACKBARS = ['/due-diligence-listing/', '/investment-memorandum-details/'];

@Injectable({ providedIn: 'root' })
export class SnackBarService {
  private isStable = false;
  private pendingTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private pendingStableSubscription: Subscription | null = null;
  private openGeneration = 0;

  constructor(
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private appRef: ApplicationRef,
    private router: Router,
  ) {
    // Wait for Angular to become stable before allowing snackbars
    // This prevents ExpressionChangedAfterItHasBeenCheckedError during bootstrap
    const stable$ = this.appRef.isStable;
    if (isObservable(stable$)) {
      stable$.pipe(first((stable) => stable === true)).subscribe(() => {
        this.isStable = true;
      });
    } else {
      this.isStable = true;
    }

    const routerEvents$ = this.router?.events;
    if (isObservable(routerEvents$)) {
      routerEvents$
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => {
          if (this.shouldClearCarryoverSnackbars(event.urlAfterRedirects)) {
            this.dismissCarryoverSnackbars();
          }
        });
    }
  }

  public open(message: string, type: AlertType): void {
    this.clearPendingOpen();
    const generation = ++this.openGeneration;

    if (!this.isStable) {
      this.pendingTimeoutId = setTimeout(() => {
        if (generation === this.openGeneration) {
          this.openSnackBar(message, type);
        }
      }, 100);

      const stable$ = this.appRef.isStable;
      if (isObservable(stable$)) {
        this.pendingStableSubscription = stable$.pipe(first((stable) => stable === true)).subscribe(() => {
          this.clearPendingTimeout();
          if (generation === this.openGeneration) {
            this.openSnackBar(message, type);
          }
        });
      }
      return;
    }
    this.openSnackBar(message, type);
  }

  private openSnackBar(message: string, type: AlertType): void {
    this.snackBar.openFromComponent(SnackBarComponent, {
      data: {
        message,
        type,
      },
      duration: type === 'danger' ? snackBarErrorDisplayTimeInMs : snackBarDisplayTimeInMs,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: `wai-snack-bar`,
    });
  }

  public success(translationKey: string, translationParams?: object): void {
    const translatedMessage = this.translate.instant(translationKey, translationParams);
    this.open(translatedMessage, 'success');
  }

  public error(translationKey: string, translationParams?: object): void {
    const translatedMessage = this.translate.instant(translationKey, translationParams);
    this.open(translatedMessage, 'danger');
  }

  public dismiss(): void {
    this.openGeneration++;
    this.clearPendingOpen();
    this.snackBar.dismiss();
  }

  /** Clears visible snackbars and cancels any still-pending opens (e.g. theme toast from org selection). */
  public dismissCarryoverSnackbars(): void {
    this.dismiss();
  }

  private clearPendingOpen(): void {
    this.clearPendingTimeout();
    this.pendingStableSubscription?.unsubscribe();
    this.pendingStableSubscription = null;
  }

  private clearPendingTimeout(): void {
    if (this.pendingTimeoutId != null) {
      clearTimeout(this.pendingTimeoutId);
      this.pendingTimeoutId = null;
    }
  }

  private shouldClearCarryoverSnackbars(url: string): boolean {
    return ROUTES_THAT_CLEAR_CARRYOVER_SNACKBARS.some((route) => url.includes(route));
  }
}
